import React, { useCallback, useState, useRef, useEffect } from 'react';
import {
  ValueContainerProps,
  MenuProps,
  OptionProps,
  DropdownIndicatorProps,
  ClearIndicatorProps,
  ContainerProps,
  components,
} from 'react-select';
import { Grid, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';

import TextField from '../TextField';
import Checkbox from '../Checkbox/Checkbox';
import Modal from '../Modal';
import { Chip } from '../Chips';
import { StatusChip } from '../StatusChip';
import { ReactComponent as ArrowDown } from '../../../../assets/icons/ArrowDown.svg';
import { ReactComponent as Clear } from '../../../../assets/icons/Clear.svg';
import { ReactComponent as Search } from '../../../../assets/icons/Search.svg';

import { FLEX_GAP_FOR_SELECTED_CHIPS, MORE_TEXT_MAX_WIDTH, useStyles } from './styles';

const SelectContainer = React.memo((props: ContainerProps<any>) => {
  const classes = useStyles();

  return (
    <components.SelectContainer {...props}>
      <label className={`${classes.label} ${props.isDisabled && classes.cursorDisabled} ${classes.labelShrink}`}>
        {props.selectProps.placeholder}
        {(props.selectProps?.['data-required'] || props.selectProps?.['data-skippable']) && (
          <span className={props.selectProps?.['data-required'] ? classes.requiredMark : classes.skippableMark}>
            {props.selectProps?.['data-required'] ? '*' : '**'}
          </span>
        )}
      </label>
      {props.children}
    </components.SelectContainer>
  );
});

const ValueContainer = React.memo(({ children, ...props }: ValueContainerProps<any>) => {
  let [values, input] = children as any;
  const classes = useStyles();
  return (
    <components.ValueContainer {...props}>
      <span className={`${classes.value} ${props.isDisabled ? classes.cursorDisabled : ''}`}>{`${values?.length || 0} ${
        values?.length === 1 ? 'value' : 'values'
      } selected`}</span>
      {/* <label
          className={`${classes.label} ${props.isDisabled &&
            classes.cursorDisabled} ${
            values?.length > 0 || props.selectProps.menuIsOpen
              ? classes.labelShrink
              : `${classes.labelBig} ${
                  props.selectProps?.["data-error"] ? classes.labelError : ""
                }`
          }`}
        >
          {props.selectProps.placeholder}
          {(props.selectProps?.["data-required"] ||
            props.selectProps?.["data-skippable"]) && (
            <span
              className={
                props.selectProps?.["data-required"]
                  ? classes.requiredMark
                  : classes.skippableMark
              }
            >
              {props.selectProps?.["data-required"] ? "*" : "**"}
            </span>
          )}
        </label> */}
      {!props.selectProps.isDisabled && input}
    </components.ValueContainer>
  );
});

const Menu = React.memo((props: MenuProps<any>) => {
  const { value, options, inputValue, onInputChange } = props.selectProps;
  const selectableOptions = props.selectProps?.['data-selectableOptions'] || options;
  const menuContentRef = props.selectProps?.['data-menuContentRef'];
  const testId = props.selectProps?.['data-testId'];
  const classes = useStyles();
  const { t } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const onClick = e => {
    e.stopPropagation();
    e.preventDefault();
  };
  const onMouseDown = e => {
    e.stopPropagation();
    e.preventDefault();
  };

  const onSelectAll = () => {
    const currentValues = value || [];
    const selectedValueSet = new Set(currentValues.map((v: any) => v.value));
    const merged = [...currentValues, ...selectableOptions.filter((o: any) => !selectedValueSet.has(o.value))];
    props.selectProps?.['data-onChange'](merged);
  };

  const onClearAll = () => {
    if (value?.length > 0) {
      props.selectProps?.['data-onChange']([]);
    }
  };

  // Callback ref to wire up the menu content wrapper to the parent's ref
  const setMenuContentRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (menuContentRef) menuContentRef.current = el;
    },
    [menuContentRef],
  );

  return (
    <components.Menu {...props}>
      <div ref={setMenuContentRef} onClick={onClick} onMouseDown={onMouseDown}>
        <div className={classes.searchbarParent}>
          <TextField
            style={{ width: '100%' }}
            className={classes.searchInput}
            placeholder="Search"
            isSearchBar={true}
            testId={testId ? `${testId}-search-input` : undefined}
            inputRef={searchInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" className={classes.dropdownIcon} disablePointerEvents>
                  <Search />
                </InputAdornment>
              ),
            }}
            onChange={e => {
              onInputChange(e.target.value, {
                action: 'input-change',
                prevInputValue: inputValue || '',
              });
            }}
            onMouseDown={e => {
              e.stopPropagation();
              // Focus the input directly on mousedown to fix Safari/macOS issues
              // where clicking adornments or padding doesn't focus the input.
              // We don't preventDefault so text selection still works if clicking the input text.
              searchInputRef.current?.focus();
            }}
            onClick={e => {
              e.stopPropagation();
              searchInputRef.current?.focus();
            }}
            onTouchEnd={e => {
              e.stopPropagation();
            }}
          />
        </div>
        <div className={classes.selectAllParent}>
          <Checkbox
            checked={selectableOptions?.length > 0 && value?.length === selectableOptions?.length}
            label={t('common.selectAll')}
            testId={testId ? `${testId}-select-all` : undefined}
            onMouseDown={onSelectAll}
          />
          <span
            className={classes.clearAll}
            data-testid={testId ? `${testId}-clear-all` : undefined}
            onClick={onClearAll}
          >
            {t('common.clearAll')}
          </span>
        </div>
        {props.children}
      </div>
    </components.Menu>
  );
});

const Option = React.memo(({ children, ...props }: OptionProps<any>) => {
  const classes = useStyles();
  const onClickOption = e => {
    props.selectOption({ ...props.data });
    e.stopPropagation();
    e.preventDefault();
  };
  const testId = props.selectProps?.['data-testId'];
  return (
    <components.Option {...props} className={classes.option}>
      <div className={classes.optionRow}>
        <Checkbox
          disabled={props.isDisabled}
          checked={props.isSelected}
          label={props.label}
          testId={testId ? `${testId}-option-${props.data?.value ?? props.label}` : undefined}
          onChange={onClickOption}
        />
        {props.data.statusChip && (
          <StatusChip label={props.data.statusChip.label} variant={props.data.statusChip.variant} />
        )}
      </div>
    </components.Option>
  );
});

const DropdownIndicator = React.memo(({ children, ...props }: DropdownIndicatorProps<any>) => {
  const classes = useStyles();
  return (
    <components.DropdownIndicator {...props}>
      <ArrowDown
        className={`${classes.dropdownIcon} ${props.selectProps.menuIsOpen ? classes.dropdownIconRotated : ''}`}
      />
    </components.DropdownIndicator>
  );
});

const ClearIndicator = React.memo(({ children, ...props }: ClearIndicatorProps<any>) => {
  const classes = useStyles();
  return (
    <components.ClearIndicator {...props}>
      <Clear className={classes.clearIcon} />
    </components.ClearIndicator>
  );
});

const SelectedChips = React.memo(({ chips, onRemove, onMoreClick }: any) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(chips.length);

  const measureChips = () => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.offsetWidth;
    const children = containerRef.current.children;

    let usedWidth = 0;
    let numVisible = 0;

    for (let i = 0; i < chips.length; i++) {
      const chipElem = children[i];
      if (!chipElem) continue;
      const chipWidth = chipElem.offsetWidth + FLEX_GAP_FOR_SELECTED_CHIPS;

      const moreWidth = MORE_TEXT_MAX_WIDTH; // Max width of the more text
      const totalWidth = usedWidth + chipWidth + (i < chips.length - 1 ? moreWidth : 0);

      if (totalWidth > containerWidth) break;

      usedWidth += chipWidth;
      numVisible++;
    }

    setVisibleCount(numVisible);
  };

  useEffect(() => {
    const debouncedMeasure = debounce(measureChips, 100);

    const rafId = requestAnimationFrame(measureChips);

    window.addEventListener('resize', () => {
      if (chips.length !== visibleCount) {
        setVisibleCount(chips.length);
      }
      debouncedMeasure();
    });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', debouncedMeasure);
      debouncedMeasure.cancel(); // cancel any pending debounced calls on cleanup
    };
  }, [chips]);

  return (
    <div ref={containerRef} className={classes.selectedChipsContainer}>
      {chips.map((chip, index) => (
        <div
          key={chip.value || chip.label}
          style={{
            whiteSpace: 'nowrap',
            ...(index < visibleCount ? {} : { position: 'absolute', top: -9999, left: -9999, opacity: 0 }),
          }}
        >
          <Chip chip={chip} variant="outlined" color="primary" hasRemove onRemove={() => onRemove(chip)} />
        </div>
      ))}

      {visibleCount < chips.length && (
        <span onClick={onMoreClick} className={classes.moreChipsText} data-testid="multiselect-more-chips">
          +{chips.length - visibleCount} {t('common.more')}
        </span>
      )}
    </div>
  );
});

const SelectedValuesModal = React.memo(({ selectedValues, onClose, onSave }: any) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [values, setValues] = useState(selectedValues || []);
  const onCheckboxValueChange = e => {
    if (e.target.checked) {
      setValues([...values, { value: e.target.value, label: e.target.name }]);
    } else {
      setValues(values.filter(v => v.value !== e.target.value));
    }
  };

  const handleSave = () => {
    onSave(values);
    onClose();
  };

  return (
    <Modal
      title={t('common.selectedValues')}
      testId="multiselect-selected-values-modal"
      modalStyleClass={classes.modal}
      Content={
        <Grid container spacing={0}>
          {selectedValues?.map(selectedValue => (
            <Grid item xs={12} md={6} lg={4} key={selectedValue.value || selectedValue.label}>
              <Checkbox
                name={selectedValue.name}
                value={selectedValue.value}
                checked={values.some(v => v.value === selectedValue.value)}
                label={selectedValue.label}
                testId={`multiselect-selected-values-option-${selectedValue.value ?? selectedValue.label}`}
                styleClass={classes.modalCheckbox}
                onChange={onCheckboxValueChange}
              />
            </Grid>
          ))}
        </Grid>
      }
      actionButtons={[
        {
          label: t('common.close'),
          variant: 'outlined',
          styleClass: classes.modalButtons,
          onClick: onClose,
        },
        {
          label: t('common.save'),
          styleClass: classes.modalButtons,
          onClick: handleSave,
        },
      ]}
      onClose={onClose}
    />
  );
});

export {
  SelectContainer,
  ValueContainer,
  Menu,
  Option,
  DropdownIndicator,
  ClearIndicator,
  SelectedChips,
  SelectedValuesModal,
};
